import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Home, MapPin, Calendar, CreditCard, TrendingUp, 
  CheckCircle, Clock, AlertCircle, 
  Filter, Search, Download, RefreshCw, Eye
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { useMySouscriptions } from '../../hooks/useClientApi';
import { useTexts } from '../../hooks/useTexts';
import Breadcrumb from '../../components/common/Breadcrumb';

export default function SubscriptionsPage() {
  // Hook pour les textes dynamiques
  const { getText } = useTexts();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState('date_souscription');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  const { data: souscriptionsData, loading, error, refetch } = useMySouscriptions({
    search: searchTerm,
    statut_souscription: statusFilter !== 'ALL' ? statusFilter : undefined,
    ordering: sortOrder === 'desc' ? `-${sortBy}` : sortBy
  });
  
  const subscriptions = souscriptionsData?.results || [];
  
  // Organiser les souscriptions par projet
  const subscriptionsByProject = subscriptions.reduce((acc, subscription) => {
    const projetNom = subscription.projet_nom || 'Projet non défini';
    
    if (!acc[projetNom]) {
      acc[projetNom] = {
        projetNom,
        subscriptions: [],
        totalPrixConvenu: 0,
        totalMontantRestant: 0,
        totalPaid: 0,
        projetAvancement: subscription.projet_avancement || 0
      };
    }
    
    acc[projetNom].subscriptions.push(subscription);
    
    // Calculer les totaux pour ce projet
    const prixConvenu = Number(subscription.prix_convenu_ht) || 0;
    const montantRestant = Number(subscription.montant_restant) || 0;
    
    acc[projetNom].totalPrixConvenu += prixConvenu;
    acc[projetNom].totalMontantRestant += montantRestant;
    acc[projetNom].totalPaid += (prixConvenu - montantRestant);
    
    return acc;
  }, {} as Record<string, any>);
  
  const projectGroups = Object.values(subscriptionsByProject) as Array<{
    projetNom: string;
    subscriptions: any[];
    totalPrixConvenu: number;
    totalMontantRestant: number;
    totalPaid: number;
    projetAvancement: number;
  }>;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'SUSPENDUE':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'TERMINEE':
        return <CheckCircle className="w-5 h-5 text-blue-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'SUSPENDUE':
        return 'bg-orange-100 text-orange-800';
      case 'TERMINEE':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonction de formatage spécifique pour le PDF
  const formatCurrencyForPDF = (amount: number) => {
    // Formatage avec point comme séparateur de milliers
    return amount.toLocaleString('fr-FR').replace(/\s/g, '.');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Fonction pour exporter les souscriptions en PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Titre du document
    doc.setFontSize(20);
    doc.text('Mes Souscriptions', 14, 22);
    
    // Informations générales
    doc.setFontSize(12);
    let yPosition = 35;
    
    doc.text(`Nombre total de souscriptions: ${subscriptions.length}`, 14, yPosition);
    yPosition += 7;
    
    const totalPrixConvenu = subscriptions.reduce((sum, sub) => sum + (Number(sub.prix_convenu_ht) || 0), 0);
    const totalMontantRestant = subscriptions.reduce((sum, sub) => sum + (Number(sub.montant_restant) || 0), 0);
    const totalPaye = totalPrixConvenu - totalMontantRestant;
    
    doc.text(`Montant total convenu: ${formatCurrencyForPDF(totalPrixConvenu)} Fcfa`, 14, yPosition);
    yPosition += 7;
    doc.text(`Montant total payé: ${formatCurrencyForPDF(totalPaye)} Fcfa`, 14, yPosition);
    yPosition += 7;
    doc.text(`Montant restant: ${formatCurrencyForPDF(totalMontantRestant)} Fcfa`, 14, yPosition);
    yPosition += 7;
    
    // Filtres appliqués
    if (statusFilter !== 'ALL') {
      const statutText = {
        'ACTIVE': 'Active',
        'SUSPENDUE': 'Suspendue',
        'TERMINEE': 'Terminée',
        'ANNULEE': 'Annulée'
      }[statusFilter] || statusFilter;
      doc.text(`Filtre par statut: ${statutText}`, 14, yPosition);
      yPosition += 7;
    }
    
    if (searchTerm) {
      doc.text(`Recherche: "${searchTerm}"`, 14, yPosition);
      yPosition += 7;
    }
    
    // Ligne de séparation
    yPosition += 5;
    doc.line(14, yPosition, 196, yPosition);
    yPosition += 10;
    
    // Préparer les données du tableau
    const tableData = subscriptions.map((subscription) => {
      return [
        subscription.projet_nom || 'N/A',
        subscription.batiment_nom || 'N/A',
        subscription.type_maison_designation || 'N/A',
        `Niveau ${subscription.niveau || 'N/A'}`,
        formatCurrencyForPDF(Number(subscription.prix_convenu_ht) || 0),
        formatCurrencyForPDF(Number(subscription.montant_restant) || 0),
        formatCurrencyForPDF((Number(subscription.prix_convenu_ht) || 0) - (Number(subscription.montant_restant) || 0)),
        (() => {
          const statusMap = {
            'ACTIVE': 'Active',
            'SUSPENDUE': 'Suspendue',
            'TERMINEE': 'Terminée',
            'ANNULEE': 'Annulée'
          };
          return statusMap[subscription.statut_souscription as keyof typeof statusMap] || subscription.statut_souscription;
        })(),
        formatDate(subscription.date_souscription),
        formatDate(subscription.date_livraison_prevue)
      ];
    });
    
    // Créer le tableau
    autoTable(doc, {
      startY: yPosition,
      head: [['Projet', 'Bâtiment', 'Type', 'Niveau', 'Prix convenu(Fcfa)', 'Restant(Fcfa)', 'Payé(Fcfa)', 'Statut', 'Date souscription', 'Livraison prévue']],
      body: tableData,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [59, 130, 246], // Bleu
        textColor: 255,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252], // Gris très clair
      },
      columnStyles: {
        0: { cellWidth: 25 }, // Projet
        1: { cellWidth: 20 }, // Bâtiment
        2: { cellWidth: 20 }, // Type
        3: { cellWidth: 15 }, // Niveau
        4: { halign: 'right', cellWidth: 20 }, // Prix convenu
        5: { halign: 'right', cellWidth: 20 }, // Restant
        6: { halign: 'right', cellWidth: 20 }, // Payé
        7: { halign: 'center', cellWidth: 15 }, // Statut
        8: { halign: 'center', cellWidth: 20 }, // Date souscription
        9: { halign: 'center', cellWidth: 20 }, // Livraison prévue
      },
      margin: { left: 14, right: 14 },
    });
    
    // Sauvegarder le PDF
    const fileName = `mes-souscriptions-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };


  // Calculer les statistiques
  const stats = {
    total: subscriptions.length,
    active: subscriptions.filter(s => s.statut_souscription === 'ACTIVE').length,
    suspended: subscriptions.filter(s => s.statut_souscription === 'SUSPENDUE').length,
    completed: subscriptions.filter(s => s.statut_souscription === 'TERMINEE').length,
    totalPaid: subscriptions.reduce((sum, s) => {
      const prixConvenu = Number(s.prix_convenu_ht) || 0;
      const montantRestant = Number(s.montant_restant) || 0;
      return sum + (prixConvenu - montantRestant);
    }, 0),
    totalRemaining: subscriptions.reduce((sum, s) => sum + (Number(s.montant_restant) || 0), 0)
  };

  // Gestion des filtres et tri
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
  };


  // États de chargement et d'erreur
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-red-800 mb-2">{getText('error.loading')}</h3>
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {getText('error.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Breadcrumb
            items={[
              { label: getText('nav.subscriptions'), current: true }
            ]}
          />
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{getText('nav.subscriptions')}</h1>
              <p className="text-gray-600 mt-2">
                {getText('subscriptions.description')}
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <button
                onClick={() => refetch()}
                className="flex items-center px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {getText('subscriptions.refresh')}
              </button>
              <button 
                onClick={exportToPDF}
                className="flex items-center px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Home className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Total Souscriptions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm text-gray-600">Actives</p>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <CreditCard className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                  <p className="text-sm text-gray-600">Total Payé</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalPaid ? Number(stats.totalPaid).toLocaleString('fr-FR') + ' FCFA' : '0 FCFA'}
                  </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                  <p className="text-sm text-gray-600">Reste à Payer</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalRemaining ? Number(stats.totalRemaining).toLocaleString('fr-FR') + ' FCFA' : '0 FCFA'}
                  </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
            {/* Recherche */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher par projet, logement..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            {/* Filtres de statut */}
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="ALL">Tous les statuts</option>
                <option value="ACTIVE">Actives</option>
                <option value="SUSPENDUE">Suspendues</option>
                <option value="TERMINEE">Terminées</option>
                <option value="ANNULEE">Annulées</option>
              </select>
            </div>
            
            {/* Tri */}
            <div className="flex items-center space-x-2">
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setSortBy(field);
                  setSortOrder(order as 'asc' | 'desc');
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="date_souscription-desc">Plus récentes</option>
                <option value="date_souscription-asc">Plus anciennes</option>
                <option value="prix_convenu_ht-desc">Prix décroissant</option>
                <option value="prix_convenu_ht-asc">Prix croissant</option>
              </select>
            </div>
          </div>
        </div>

        {/* Subscriptions List */}
        <div className="space-y-6">
          {projectGroups.map((projectGroup) => (
            <div key={projectGroup.projetNom} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Project Header */}
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {projectGroup.projetNom}
                    </h2>
                    <div className="flex items-center text-sm text-gray-500 mt-2">
                      <span className="mr-4">{projectGroup.subscriptions.length} souscription{projectGroup.subscriptions.length > 1 ? 's' : ''}</span>
                      <span>Avancement: {projectGroup.projetAvancement}%</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-blue-600">
                      {Number(projectGroup.totalPrixConvenu).toLocaleString('fr-FR')} FCFA
                    </div>
                    <div className="text-sm text-gray-500">Investissement total</div>
                  </div>
                </div>
                
                {/* Project Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${projectGroup.projetAvancement}%` }}
                  />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Financial Summary */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Résumé Financier du Projet</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Investissement total:</span>
                        <span className="font-semibold">
                          {Number(projectGroup.totalPrixConvenu).toLocaleString('fr-FR')} FCFA
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Montant payé:</span>
                        <span className="font-semibold text-green-600">
                          {Number(projectGroup.totalPaid).toLocaleString('fr-FR')} FCFA
                        </span>
                      </div>
                      <div className="flex justify-between border-t border-gray-200 pt-3">
                        <span className="text-gray-600">Reste à payer:</span>
                        <span className="font-bold text-orange-600">
                          {Number(projectGroup.totalMontantRestant).toLocaleString('fr-FR')} FCFA
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Progress & Schedule */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Progression</h3>
                    
                    {/* Project Progress */}
                    {/* <div className="bg-gray-50 p-4 rounded-lg mb-4">
                      <div className="flex items-center mb-2">
                        <TrendingUp className="w-5 h-5 text-gray-600 mr-2" />
                        <span className="font-medium text-gray-900">Avancement du projet</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>{projectGroup.projetAvancement}% terminé</p>
                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${projectGroup.projetAvancement}%` }}
                          />
                        </div>
                      </div>
                    </div> */}

                    {/* Next Payment Info */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                        <span className="font-medium text-blue-900">Prochaines échéances</span>
                      </div>
                      <div className="text-sm text-blue-800">
                        <p>Consultez les détails de chaque souscription ci-dessous</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Individual Subscriptions */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Souscriptions individuelles</h3>
                <div className="space-y-4">
                  {projectGroup.subscriptions.map((subscription: any) => (
                    <div key={subscription.id} className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900">{subscription.type_maison_designation || 'Type non défini'}</h4>
                          <div className="flex items-center text-sm text-gray-600 mt-1">
                            <MapPin className="w-4 h-4 mr-1" />
                            <span>{subscription.batiment_nom || 'Bâtiment non défini'}{subscription.niveau !== null && subscription.niveau !== undefined ? ` - Niveau ${subscription.niveau}` : ''}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(subscription.statut_souscription)}
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription.statut_souscription)}`}>
                            {subscription.statut_souscription}
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Prix convenu:</span>
                          <span className="ml-2 font-medium">
                            {subscription.prix_convenu_ht ? Number(subscription.prix_convenu_ht).toLocaleString('fr-FR') + ' FCFA' : '0 FCFA'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Reste à payer:</span>
                          <span className="ml-2 font-medium text-orange-600">
                            {subscription.montant_restant ? Number(subscription.montant_restant).toLocaleString('fr-FR') + ' FCFA' : '0 FCFA'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Date souscription:</span>
                          <span className="ml-2 font-medium">
                            {new Date(subscription.date_souscription).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Mode acquisition:</span>
                          <span className="ml-2 font-medium">
                            {subscription.mode_acquisition || 'Non défini'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Link
                          to={`/subscriptions/${subscription.id}`}
                          className="flex items-center px-3 py-1 text-sm text-blue-600 bg-blue-100 rounded hover:bg-blue-200 transition-colors"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Voir détails
                        </Link>
                        <Link
                          to={`/payments?subscription=${subscription.id}`}
                          className="flex items-center px-3 py-1 text-sm text-green-600 bg-green-100 rounded hover:bg-green-200 transition-colors"
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          Paiements
                        </Link>
                        {/* <button className="flex items-center px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors">
                          <FileText className="w-4 h-4 mr-1" />
                          Documents
                        </button> */}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          ))}
        </div>

        {/* Empty State */}
        {projectGroups.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Home className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Aucune souscription
            </h3>
            <p className="text-gray-600 mb-6">
              Vous n'avez pas encore souscrit à un projet immobilier
            </p>
            <Link
              to="/projects"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Découvrir nos projets
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}